import { Routes } from '@angular/router';
import { permissionGuard } from '../guards/permission.guard';
import { paymentFeatureGuard } from '../guards/payment-feature.guard';
import { settingsIndexGuard } from '../guards/settings-index.guard';

/**
 * Shared settings-area routes nested under SettingsLayoutComponent.
 *
 * Canonical URLs (same pattern as marketplace):
 *   /:base/settings
 *   /:base/settings/integrations
 *   /:base/settings/members
 *   /:base/settings/billing
 *   /:base/settings/billing/invoices
 *   /:base/settings/usage
 *   /:base/settings/account
 *   /:base/settings/account/:section  (security, preferences, …)
 *
 * Flat legacy URLs (/members, /billing, /usage) redirect here.
 * One shell for every persona — org items are permission-gated; account is open.
 */
export const SETTINGS_SHELL_ROUTES: Routes = [
  {
    path: 'settings',
    data: { minimalShell: true },
    loadComponent: () =>
      import('../../layouts/settings-layout/settings-layout.component').then(
        (m) => m.SettingsLayoutComponent,
      ),
    children: [
      // Workspace general when `settings.manage`; otherwise redirect to account
      // so learners/teachers/editors share the same shell chrome.
      {
        path: '',
        pathMatch: 'full',
        canActivate: [settingsIndexGuard],
        loadComponent: () =>
          import('../../pages/workspaces/shared/settings/settings.page').then(
            (m) => m.WorkspaceSettingsPage,
          ),
      },
      {
        path: 'integrations',
        canActivate: [permissionGuard],
        data: { permission: 'settings.manage' },
        loadComponent: () =>
          import('../../pages/workspaces/shared/settings/settings.page').then(
            (m) => m.WorkspaceSettingsPage,
          ),
      },
      {
        path: 'members',
        canActivate: [permissionGuard],
        data: { permission: 'members.manage' },
        loadComponent: () =>
          import('../../pages/workspaces/shared/members/members.page').then(
            (m) => m.MembersPage,
          ),
      },
      {
        path: 'billing/invoices',
        canActivate: [paymentFeatureGuard, permissionGuard],
        data: { permission: 'billing.manage' },
        loadComponent: () =>
          import(
            '../../pages/workspaces/shared/billing/invoices/billing-invoices.page'
          ).then((m) => m.BillingInvoicesPage),
      },
      {
        path: 'billing',
        canActivate: [paymentFeatureGuard, permissionGuard],
        data: { permission: 'billing.manage' },
        loadComponent: () =>
          import('../../pages/workspaces/shared/billing/billing.page').then(
            (m) => m.BillingPage,
          ),
      },
      {
        path: 'usage',
        canActivate: [permissionGuard],
        data: { permission: 'usage.view' },
        loadComponent: () =>
          import('../../pages/workspaces/shared/usage/usage.page').then(
            (m) => m.UsagePage,
          ),
      },
      {
        path: 'account',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            '../../pages/workspaces/shared/settings/account/account-settings.page'
          ).then((m) => m.AccountSettingsPage),
      },
      {
        path: 'account/:section',
        loadComponent: () =>
          import(
            '../../pages/workspaces/shared/settings/account/account-settings.page'
          ).then((m) => m.AccountSettingsPage),
      },
    ],
  },
  // Legacy flat URLs → /settings/*
  { path: 'members', redirectTo: 'settings/members', pathMatch: 'full' },
  { path: 'billing', redirectTo: 'settings/billing', pathMatch: 'full' },
  {
    path: 'billing/invoices',
    redirectTo: 'settings/billing/invoices',
    pathMatch: 'full',
  },
  { path: 'usage', redirectTo: 'settings/usage', pathMatch: 'full' },
];
